import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  Input,
  Label,
} from 'reactstrap';
import TableContainer from '../../components/Common/TableContainer';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import PageGuard from '../../components/Common/PageGuard';
import PermissionGate from '../../components/Common/PermissionGate';
import { GET_USERS, GetUsersData, GetUsersVariables } from '../../graphql/queries/users';
import { UPDATE_USER, UpdateUserVariables, DELETE_USER, DeleteUserVariables } from '../../graphql/mutations/users';
import { useAuth } from '../../contexts/AuthContext';

const UsersList: React.FC = () => {
  document.title = 'Users List | NGK Tours';

  const { hasCapability, isSuperAdmin } = useAuth();
  const [pageSize] = useState(10);
  const [approvingUserId, setApprovingUserId] = useState<number | null>(null);
  const [suspendingUserId, setSuspendingUserId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');

  // Map WordPress role names to GraphQL UserRoleEnum values
  const roleToEnum = (role: string): string => {
    const enumMap: Record<string, string> = {
      suspended_user: 'SUSPENDED',
      pending_user: 'PENDING_USER',
      rejected_user: 'REJECTED_USER',
    };
    const lower = role.toLowerCase();
    if (enumMap[lower]) return enumMap[lower];
    return role.toUpperCase().replace(/-/g, '_');
  };

  // Debounce search term to avoid querying on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build where clause for GraphQL query
  const whereClause = useMemo(() => {
    const where: any = {};
    
    if (selectedRole) {
      // Convert role to GraphQL enum format
      where.roleIn = [roleToEnum(selectedRole)];
    }
    
    if (debouncedSearchTerm.trim()) {
      where.search = debouncedSearchTerm.trim();
    }
    
    return Object.keys(where).length > 0 ? where : undefined;
  }, [selectedRole, debouncedSearchTerm]);

  // Fetch users from GraphQL
  const queryResult = useQuery<GetUsersData, GetUsersVariables>(
    GET_USERS,
    {
      variables: {
        first: 100, // Increase to show more users when filtering
        where: whereClause,
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    }
  );
  const { data, loading, error, refetch } = queryResult;

  // Get all available roles for filter dropdown (from all users, not filtered)
  const availableRoles = useMemo(() => {
    const rolesSet = new Set<string>();
    // Common roles that might exist
    const commonRoles = [
      'super_admin_business',
      'admin_business',
      'reservation_business',
      'account_business',
      'operations_business',
      'agent_business',
      'subscriber',
      'pending_user',
      'rejected_user',
      'suspended_user',
    ];
    commonRoles.forEach((role) => rolesSet.add(role));
    
    // Also add roles from current data
    if (data?.users?.nodes) {
      data.users.nodes.forEach((user) => {
        user.roles.nodes.forEach((role) => {
          rolesSet.add(role.name);
        });
      });
    }
    return Array.from(rolesSet).sort();
  }, [data]);

  // Approve user mutation (uses UPDATE_USER to remove pending_user role)
  const [approveUser] = useMutation<unknown, UpdateUserVariables>(UPDATE_USER, {
    refetchQueries: [{ query: GET_USERS }],
    onCompleted: () => {
      setApprovingUserId(null);
    },
    onError: (err: any) => {
      console.error('Error approving user:', err);
      setApprovingUserId(null);
      const errorMsg = err.graphQLErrors?.[0]?.extensions?.debugMessage || err.message;
      alert('Failed to approve user: ' + errorMsg);
    },
  });

  // Suspend user mutation (separate hook to avoid conflicts)
  const [suspendUser] = useMutation<unknown, UpdateUserVariables>(UPDATE_USER, {
    refetchQueries: [{ query: GET_USERS }],
    onCompleted: () => {
      setSuspendingUserId(null);
    },
    onError: (err: any) => {
      console.error('Error suspending user:', err);
      console.error('Full error details:', JSON.stringify(err, null, 2));
      setSuspendingUserId(null);
      // Extract detailed error message
      const graphQLError = err.graphQLErrors?.[0];
      const errorMsg = graphQLError?.extensions?.debugMessage || graphQLError?.message || err.message;
      alert('Failed to suspend user: ' + errorMsg);
    },
  });

  // Handle approve user
  const handleApproveUser = (user: any) => {
    if (!window.confirm(`Approve user "${user.name}"?`)) return;

    setApprovingUserId(user.id);
    
    // Filter out pending_user and rejected_user roles, keep business role
    const businessRoles = user.roles.filter(
      (role: string) => 
        !role.includes('pending_user') && 
        !role.includes('rejected_user') &&
        role.includes('business')
    );

    // If no business role found, keep the first non-pending role
    const rolesToKeep = businessRoles.length > 0 
      ? businessRoles 
      : user.roles.filter((role: string) => !role.includes('pending') && !role.includes('rejected'));

    approveUser({
      variables: {
        input: {
          id: user.userId,
          roles: rolesToKeep.length > 0 ? rolesToKeep : ['subscriber'], // Fallback to subscriber
        },
      },
    });
  };

  // Delete user mutation
  const [deleteUser] = useMutation<unknown, DeleteUserVariables>(DELETE_USER, {
    refetchQueries: [{ query: GET_USERS }],
    onCompleted: () => {
      setDeletingUserId(null);
    },
    onError: (err) => {
      console.error('Error deleting user:', err);
      setDeletingUserId(null);
      alert('Failed to delete user: ' + err.message);
    },
  });

  const handleDeleteUser = (user: any) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingUserId(user.id);

    deleteUser({
      variables: {
        input: {
          id: user.userId,
        },
      },
    });
  };

  // Handle suspend user
  const handleSuspendUser = (user: any) => {
    if (!window.confirm(`Suspend user "${user.name}"?`)) return;

    setSuspendingUserId(user.id);

    // Start from existing roles and remove status-specific roles
    const currentRoles: string[] = user.roles || [];
    const baseRoles = currentRoles.filter(
      (role: string) =>
        !role.includes('pending_user') &&
        !role.includes('rejected_user') &&
        !role.includes('suspended_user')
    );

    // Add suspended_user marker role
    const newRoles = [...baseRoles, 'suspended_user'];

    suspendUser({
      variables: {
        input: {
          id: user.userId,
          roles: newRoles.length > 0 ? newRoles : ['suspended_user'],
        },
      },
    });
  };

  // Handle unsuspend user (approve suspended user)
  const handleUnsuspendUser = (user: any) => {
    if (!window.confirm(`Approve (unsuspend) user "${user.name}"?`)) return;

    setSuspendingUserId(user.id);

    // Remove suspended_user role, keep all other roles
    const currentRoles: string[] = user.roles || [];
    const newRoles = currentRoles.filter(
      (role: string) => !role.includes('suspended_user')
    );

    // Ensure user has at least subscriber role
    const finalRoles = newRoles.length > 0 ? newRoles : ['subscriber'];

    suspendUser({
      variables: {
        input: {
          id: user.userId,
          roles: finalRoles,
        },
      },
    });
  };

  // Format users data for table
  const users = useMemo(() => {
    if (!data?.users?.nodes) return [];
    
    return data.users.nodes.map((user) => ({
      id: user.databaseId,
      userId: user.id,
      name: user.name || user.username || 'N/A',
      email: user.email,
      username: user.username,
      roles: user.roles.nodes.map((role) => role.name),
      registeredDate: user.registeredDate
        ? new Date(user.registeredDate).toLocaleDateString()
        : 'N/A',
    }));
  }, [data]);

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: '#',
        accessor: 'id',
        width: '5%',
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (cellProps: any) => {
          const user = cellProps.row.original;
          return (
            <div>
              <h5 className="font-size-14 mb-1">
                <Link to={`/users/${user.id}`} className="text-dark">
                  {user.name}
                </Link>
              </h5>
              <p className="text-muted mb-0">{user.email}</p>
            </div>
          );
        },
      },
      {
        Header: 'Username',
        accessor: 'username',
      },
      {
        Header: 'Status',
        accessor: 'roles',
        Cell: (cellProps: any) => {
          const roles = cellProps.row.original.roles || [];
          const isPending = roles.includes('pending_user');
          const isRejected = roles.includes('rejected_user');
          const isSuspended = roles.includes('suspended_user');
          const businessRoles = roles.filter((r: string) => r.includes('business'));
          
          return (
            <div className="d-flex flex-wrap gap-1">
              {isPending && (
                <Badge color="warning" className="mb-1">
                  Pending
                </Badge>
              )}
              {isRejected && (
                <Badge color="danger" className="mb-1">
                  Rejected
                </Badge>
              )}
              {isSuspended && (
                <Badge color="secondary" className="mb-1">
                  Suspended
                </Badge>
              )}
              {!isPending && !isRejected && !isSuspended && businessRoles.length > 0 && (
                <Badge color="success" className="mb-1">
                  Approved
                </Badge>
              )}
              {businessRoles.map((role: string) => (
                <Badge
                  key={role}
                  color={
                    role.includes('super_admin')
                      ? 'danger'
                      : role.includes('admin')
                      ? 'warning'
                      : 'info'
                  }
                  className="mb-1"
                >
                  {role}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        Header: 'Registered',
        accessor: 'registeredDate',
      },
      {
        Header: 'Action',
        Cell: (cellProps: any) => {
          const user = cellProps.row.original;
          const isPending = user.roles.includes('pending_user');
          const isSuspended = user.roles.includes('suspended_user');
          const isApproving = approvingUserId === user.id;
          const isSuspending = suspendingUserId === user.id;
          const isDeleting = deletingUserId === user.id;

          return (
            <div className="d-flex gap-2">
              {isPending && (
                <PermissionGate
                  requiredCapabilities={['edit_users', 'promote_users']}
                >
                  <Button
                    size="sm"
                    color="success"
                    onClick={() => handleApproveUser(user)}
                    disabled={isApproving}
                  >
                    {isApproving ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-check"></i>
                    )}
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate
                requiredCapabilities={['business_user_suspend']}
              >
                {isSuspended ? (
                  <Button
                    size="sm"
                    color="success"
                    onClick={() => handleUnsuspendUser(user)}
                    disabled={isSuspending}
                    title="Approve (unsuspend) user"
                  >
                    {isSuspending ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-check"></i>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    color="warning"
                    onClick={() => handleSuspendUser(user)}
                    disabled={isSuspending}
                    title="Suspend user"
                  >
                    {isSuspending ? (
                      <Spinner size="sm" />
                    ) : (
                      <i className="mdi mdi-account-off"></i>
                    )}
                  </Button>
                )}
              </PermissionGate>
              <PermissionGate
                requiredCapabilities={['edit_users']}
              >
                <Link
                  to={`/users/${user.userId}/edit`}
                  className="btn btn-sm btn-primary"
                  title="Edit user"
                >
                  <i className="mdi mdi-pencil"></i>
                </Link>
              </PermissionGate>
              <PermissionGate
                requiredCapabilities={['delete_users']}
              >
                <Button
                  size="sm"
                  color="danger"
                  onClick={() => handleDeleteUser(user)}
                  disabled={isDeleting}
                  title="Delete user"
                >
                  {isDeleting ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="mdi mdi-delete"></i>
                  )}
                </Button>
              </PermissionGate>
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <PageGuard
      requiredCapabilities={['create_users']}
      anyCapability={['business_role_manage', 'business_assign_predefined_roles']}
    >
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Users" breadcrumbItem="User List" />

          <Row className="align-items-center mb-3">
            <Col md={6}>
              <div>
                <h5 className="card-title">
                  Users List{' '}
                  <span className="text-muted fw-normal ms-2">
                    ({data?.users?.nodes?.length || 0})
                  </span>
                </h5>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                <PermissionGate
                  requiredCapabilities={['create_users']}
                  anyCapability={['business_role_manage', 'business_assign_predefined_roles']}
                >
                  <Link to="/users-create" className="btn btn-primary">
                    <i className="bx bx-plus me-1"></i> Add New User
                  </Link>
                </PermissionGate>
                <Button
                  color="light"
                  onClick={() => refetch()}
                  disabled={loading}
                >
                  <i className="bx bx-refresh me-1"></i> Refresh
                </Button>
              </div>
            </Col>
          </Row>

          {/* Search and Filter Row */}
          <Row className="mb-3">
            <Col md={6}>
              <Label>Search Users</Label>
              <div className="d-flex gap-2">
                <Input
                  type="text"
                  placeholder="Search by name, email, or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
                {searchTerm && (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    <i className="mdi mdi-close"></i>
                  </Button>
                )}
              </div>
            </Col>
            <Col md={6}>
              <Label>Filter by Role</Label>
              <div className="d-flex gap-2">
                <Input
                  type="select"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="form-control"
                >
                  <option value="">All Roles</option>
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Input>
                {selectedRole && (
                  <Button
                    color="light"
                    size="sm"
                    onClick={() => setSelectedRole('')}
                    title="Clear filter"
                  >
                    <i className="mdi mdi-close"></i>
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {loading && (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2">Loading users...</p>
                    </div>
                  )}

                  {error && (
                    <Alert color="danger">
                      <h5>Error loading users</h5>
                      <p>{error.message}</p>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => refetch()}
                      >
                        Retry
                      </Button>
                    </Alert>
                  )}

                  {!loading && !error && (
                    <>
                      {users.length === 0 ? (
                        <div className="text-center py-4">
                          <p className="text-muted">No users found</p>
                        </div>
                      ) : (
                        <TableContainer
                          columns={columns}
                          data={users}
                          isGlobalFilter={false}
                          customPageSize={pageSize}
                          className="table align-middle datatable dt-responsive table-check nowrap"
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </PageGuard>
  );
};

export default UsersList;
